import { getStoredMeta, loadStoredSession } from "../auth/auth.storage";

interface NotificationEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  status: string;
  readAt?: string | null;
  createdAt: string;
  data?: {
    module?: string;
    actionUrl?: string;
    [key: string]: unknown;
  };
}

export interface NotificationListResult {
  items: NotificationItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export class NotificationApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "NotificationApiError";
  }
}

function baseUrl(): string {
  return (
    import.meta.env.VITE_NOTIFICATION_API_BASE_URL ?? "/api/notifications"
  ).replace(/\/$/, "");
}

async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const { accessToken } = getStoredMeta();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const slug = loadStoredSession()?.tenant?.slug;
  if (slug) {
    headers["X-Tenant-Slug"] = slug;
  }

  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl()}${path}`, {
    ...init,
    headers: {
      ...(await buildHeaders()),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });

  const text = await response.text();
  const body = text.trim()
    ? (JSON.parse(text) as NotificationEnvelope<T> | { message?: string })
    : null;

  if (!response.ok) {
    const message =
      body && typeof body === "object" && "message" in body && body.message
        ? String(body.message)
        : `Notifications API error (${response.status})`;
    throw new NotificationApiError(message, response.status);
  }

  if (!body || !("success" in body) || !body.success) {
    throw new NotificationApiError("Invalid notifications API response", response.status);
  }

  return body.data;
}

export const notificationApi = {
  list(page = 1, limit = 20) {
    return request<NotificationListResult>(
      `/notifications?page=${page}&limit=${limit}`,
    );
  },

  unreadCount() {
    return request<{ count: number }>("/notifications/unread-count");
  },

  markRead(id: string) {
    return request<{ updated: number }>(`/notifications/${id}/read`, {
      method: "PATCH",
    });
  },

  markAllRead() {
    return request<{ updated: number }>("/notifications/read-all", {
      method: "PATCH",
    });
  },
};
